import React from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ChevronDown } from "lucide-react";
import { cn } from "util/cn";

interface DetailPanelProps {
    title?: string;
    titleClassName?: string;
    children: React.ReactNode;
    className?: string;
    defaultOpen?: boolean;
    headingContent?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    showTopCollapse?: boolean;
    showBottomCollapse?: boolean;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ 
    title,
    titleClassName,
    children, 
    className,
    defaultOpen = true,
    headingContent,
    open,
    onOpenChange,
    showTopCollapse = true,
    showBottomCollapse = false
}) => {
    // Use controlled or uncontrolled component based on whether open state is provided
    const isControlled = open !== undefined && onOpenChange !== undefined;
    const headerContent = (
        <div>
            <div className="flex flex-row gap-2 group w-full items-center justify-between">
                {title && (
                    <span className={cn("font-semibold", titleClassName)}>{title}</span>
                )}
                {showTopCollapse && (
                    <ChevronDown 
                        className="h-4 w-4 shrink-0 text-eoe-text-sub dark:text-eoe-dark-text-sub transition-transform duration-200 group-data-[state=open]:rotate-180" 
                    />
                )}
            </div>
            {headingContent && (
                headingContent
            )}
        </div>
    );
    
    return (
        <Collapsible.Root 
            defaultOpen={defaultOpen} 
            open={isControlled ? open : undefined}
            onOpenChange={isControlled ? onOpenChange : undefined}
            className={cn("w-full rounded-lg", className)}
        >
            {showTopCollapse ? (
                <Collapsible.Trigger className="group flex flex-col w-full p-2 space-y-2 text-sm text-eoe-text dark:text-eoe-dark-text rounded-lg transition-colors">
                    {headerContent}
                </Collapsible.Trigger>
            ) : (
                <div className="group flex flex-col w-full p-2 space-y-2 text-sm text-eoe-text dark:text-eoe-dark-text rounded-lg transition-colors">
                    {headerContent}
                </div>
            )}
            <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-collapse data-[state=open]:animate-expand">
                <div className="detail-panel-content">
                    {children}
                </div>
            </Collapsible.Content>
            {showBottomCollapse && (
                <Collapsible.Trigger className="group flex flex-col p-1 w-full items-center justify-between bg-eoe-icon-hover dark:bg-eoe-dark-icon-hover rounded-lg">
                    <ChevronDown 
                        className="size-5 shrink-0 text-eoe-text-sub dark:text-eoe-dark-text-sub transition-transform duration-200 group-data-[state=open]:rotate-180" 
                    />
                </Collapsible.Trigger>
            )}
        </Collapsible.Root>
    );
};

export default DetailPanel;
